import { NavCategory } from '../enums/NavCategory.enum';
import { Color } from '../enums/Color.enum';
import { Fit } from '../enums/Fit.enum';
import { DataLayerEvent, DataLayerProduct, DataLayerPromotion, DataLayerVariant, ProductPayload } from '../models/DataLayer.model';
import { HoneyloveWindow } from '../models/HoneyloveWindow';
import { Shopify } from '../models/Shopify.model';
import { ShopifyCheckout } from '../models/ShopifyCheckout.model';
import { LongSize } from '../product_detail/components/size.enum';

declare const window: HoneyloveWindow;

export const productClicked = (shopify: Shopify | undefined, pathname: string, product: DataLayerProduct): DataLayerEvent => ({
  event: 'ProductClicked',
  ecommerce: {
    currencyCode: currencyCode(shopify),
    click: {
      actionField: { list: pageContext(pathname) },
      products: [productPayload({ product })],
    },
  },
});

// note: this is a minimal view and not a detail view
export const productViewed = (shopify: Shopify | undefined, productList: ProductPayload[]): DataLayerEvent => ({
  event: 'ProductViewed',
  ecommerce: {
    currencyCode: currencyCode(shopify),
    impressions: productList.map(({ product, variant }) => productPayload({ product, variant })),
  },
});

export const productDetailViewed = (
  shopify: Shopify | undefined,
  pathname: string,
  product: DataLayerProduct,
  variant: DataLayerVariant | undefined,
  color: Color | undefined,
  size: LongSize | undefined,
): DataLayerEvent => ({
  event: 'ProductDetailViewed',
  ecommerce: {
    currencyCode: currencyCode(shopify),
    detail: {
      actionField: { list: pageContext(pathname) },
      products: [productPayload({ product, variant, color, size })],
    },
  },
});

export const productListViewed = (shopify: Shopify | undefined, products: DataLayerProduct[], list: NavCategory | string): DataLayerEvent => ({
  event: 'ProductListViewed',
  ecommerce: {
    currencyCode: currencyCode(shopify),
    impressions: products.map((p) => ({ ...productPayload({ product: p }), list })),
  },
});

export const productColorChanged = (
  shopify: Shopify | undefined,
  product: DataLayerProduct,
  variant: DataLayerVariant | undefined,
  color: Color,
  size: LongSize | undefined,
): DataLayerEvent => ({
  event: 'ProductColorChanged',
  ecommerce: {
    currencyCode: currencyCode(shopify),
    custom: {
      products: [productPayload({ product, variant, color, size })],
    },
  },
});

export const productSizeChanged = (
  shopify: Shopify | undefined,
  product: DataLayerProduct,
  variant: DataLayerVariant | undefined,
  color: Color | undefined,
  size: LongSize | undefined,
  fit: Fit | undefined,
): DataLayerEvent => ({
  event: 'ProductSizeChanged',
  ecommerce: {
    currencyCode: currencyCode(shopify),
    custom: {
      products: [productPayload({ product, variant, color, size, fit })],
    },
  },
});

export const productStyleChanged = (
  shopify: Shopify | undefined,
  product: DataLayerProduct,
  variant: DataLayerVariant | undefined,
  color: Color,
  size: LongSize | undefined,
): DataLayerEvent => ({
  event: 'ProductStyleChanged',
  ecommerce: {
    currencyCode: currencyCode(shopify),
    custom: {
      products: [productPayload({ product, variant, color, size })],
    },
  },
});

export const cartItemAdded = (
  shopify: Shopify | undefined,
  _pathname: string,
  product: DataLayerProduct,
  variant: DataLayerVariant,
  quantity: number,
): DataLayerEvent => ({
  event: 'CartItemAdded',
  ecommerce: {
    currencyCode: currencyCode(shopify),
    add: {
      actionField: { list: 'Cart' },
      products: [productPayload({ product: product, variant }, quantity)],
    },
  },
});

export const cartItemRemoved = (shopify: Shopify | undefined, pathname: string, product: DataLayerProduct, variant: DataLayerVariant): DataLayerEvent => ({
  event: 'CartItemRemoved',
  ecommerce: {
    currencyCode: currencyCode(shopify),
    remove: {
      actionField: { list: 'Cart' },
      products: [productPayload({ product: product, variant }, 1)],
    },
  },
});

export const outOfStockRedirectViewed = (): DataLayerEvent => ({ event: 'OutOfStockRedirectViewed' });

export const outOfStockRedirectClicked = (): DataLayerEvent => ({ event: 'OutOfStockRedirectClicked' });

export const sizingToolStarted = (): DataLayerEvent => ({ event: 'SizingToolStarted' });

export const sizingToolCompleted = (): DataLayerEvent => ({ event: 'SizingToolCompleted' });

export const sizingToolStyleSelected = (_product: DataLayerProduct): DataLayerEvent => ({ event: 'SizingToolProductSelected' });

export const viewItInMySizeToolOpened = (): DataLayerEvent => ({ event: 'ViewItInMySizeToolOpened' });

const navigationLinkLocations = (location?: string) => {
  switch (location) {
    case 'header':
      return 'Header';
    case 'footer':
      return 'Footer';
    case 'cta':
      return 'CTA';
    case 'mobileMenu':
      return 'Mobile Menu';
    default:
      return location;
  }
};

export const navigationLinkClicked = (label?: string, location?: string): DataLayerEvent => ({
  event: 'NavigationLinkClicked',
  label,
  location: navigationLinkLocations(location),
});

export const userDetected = (id: string, email: string): DataLayerEvent => ({
  event: 'UserDetected',
  id,
  email,
});

export const reactPageLoaded = (label: string): DataLayerEvent => ({
  event: 'ReactPageLoaded',
  label,
});

export const swimlaneLoaded = (lane: number, label?: string): DataLayerEvent => ({
  event: `Swimlane${lane}Loaded`,
  label,
});

export const checkoutStepEncountered = (shopify: Shopify | undefined, productSets: ProductPayload[], step: string, option: string): DataLayerEvent => ({
  event: 'CheckoutStepEncountered',
  ecommerce: {
    currencyCode: currencyCode(shopify),
    checkout: {
      actionField: {
        step,
        option,
      },
      products: productSets.map(lineItemPayloadToProduct).map(productPayload),
    },
  },
});

export const checkoutValidationErrorEncountered = (form: string, field: string): DataLayerEvent => ({
  event: 'CheckoutValidationErrorEncountered',
  form,
  field,
});

export const formInteraction = (form: string, field: string): DataLayerEvent => ({
  event: 'FormInteraction',
  form,
  field,
});

export const referalPromoViewed = (): DataLayerEvent =>
  promoViewed({
    id: 'RC-PPW',
    name: 'Referral Candy Post Purchase Widget',
    creative: 'Honeylove Referral V2 Theme',
  });

export const referalPromoClicked = (): DataLayerEvent =>
  promoClicked({
    id: 'RC-PPW',
    name: 'Referral Candy Post Purchase Widget',
    creative: 'Honeylove Referral V2 Theme',
  });

export const cartPromoClicked = (): DataLayerEvent =>
  promoClicked({
    id: 'CART-PROMO-LIST',
    name: 'Cart Upsell',
  });

export const itemUpsellPromoViewed = (): DataLayerEvent =>
  promoViewed({
    id: 'ITEM-UPSELL',
    name: 'Item Upsell',
  });

export const itemUpsellPromoClicked = (): DataLayerEvent =>
  promoClicked({
    id: 'ITEM-UPSELL',
    name: 'Item Upsell',
  });

export const promoViewed = ({ id, name, creative }: DataLayerPromotion): DataLayerEvent => ({
  event: 'PromoViewed',
  ecommerce: {
    promoView: {
      promotions: [removeUndefinedValues({ id, name, creative })],
    },
  },
});

export const promoClicked = ({ id, name, creative }: DataLayerPromotion): DataLayerEvent => ({
  event: 'PromoClicked',
  ecommerce: {
    promoClick: {
      promotions: [removeUndefinedValues({ id, name, creative })],
    },
  },
});

export const desktopNavMenuOpened = (label?: string): DataLayerEvent => ({
  event: 'DesktopNavMenuOpened',
  label,
});

export const mobileNavMenuOpened = (): DataLayerEvent => ({ event: 'MobileNavMenuOpened' });

export const videoEngaged = (): DataLayerEvent => ({ event: 'VideoEngaged' });

export const emailSubscriptionStarted = (): DataLayerEvent => ({ event: 'EmailSubscriptionStarted' });

export const emailSubscriptionCompleted = (): DataLayerEvent => ({ event: 'EmailSubscriptionCompleted' });

export const purchase = (shopifyCheckout: ShopifyCheckout, productSets: ProductPayload[]): DataLayerEvent => ({
  event: 'Purchase',
  email: shopifyCheckout.email,
  ecommerce: {
    currencyCode: shopifyCheckout.presentment_currency || 'USD',
    purchase: {
      actionField: {
        id: shopifyCheckout.order_id,
        affiliation: 'Online Store',
        revenue: shopifyCheckout.total_price,
        tax: shopifyCheckout.total_tax,
        shipping: (shopifyCheckout && shopifyCheckout.shipping_rate && shopifyCheckout.shipping_rate.price) || 0,
        shippingOption: shopifyCheckout && shopifyCheckout.shipping_rate && shopifyCheckout.shipping_rate.title,
        coupon: shopifyCheckout.discount && shopifyCheckout.discount.code,
      },
      products: productSets.map((set) => productPayload(lineItemPayloadToProduct(set))),
    },
  },
});

export const paymentGatewayUsed = (gateway: string): DataLayerEvent => ({
  event: 'PaymentGatewayUsed',
  paymentGateway: gateway,
});

export const userSignedIn = (): DataLayerEvent => ({ event: 'UserSignedIn' });

export const userSignedOut = (): DataLayerEvent => ({ event: 'UserSignedOut' });

export const userRegistered = (): DataLayerEvent => ({ event: 'UserRegistered' });

export const productImageViewed = (productTitle: string): DataLayerEvent => ({
  event: 'ProductImageViewed',
  label: productTitle,
});

export const purchaseRefererTest = (transactionId: string): DataLayerEvent => ({
  event: 'PurchaseRefererTest',
  label: transactionId,
});

export const purchaseTimingTest = (transactionId: string): DataLayerEvent => ({
  event: 'PurchaseTimingTest',
  label: transactionId,
});

export const purchaseDualTest = (transactionId: string): DataLayerEvent => ({
  event: 'purchaseDualTest',
  label: transactionId,
});

export const sizingToolUserResponseEvent = ({ sizingToolUserResponse, handle }: Pick<DataLayerEvent, 'sizingToolUserResponse' | 'handle'>): DataLayerEvent => ({
  event: 'SizingToolResponse',
  sizingToolUserResponse,
  handle,
});

export const quizToolUserResponseEvent = ({ quizToolUserResponse }: Pick<DataLayerEvent, 'quizToolUserResponse'>): DataLayerEvent => ({
  event: 'QuizToolResponse',
  quizToolUserResponse,
});

export const postPurchaseUserResponseEvent = ({ postPurchaseUserResponse }: Pick<DataLayerEvent, 'postPurchaseUserResponse'>): DataLayerEvent => ({
  event: 'PostPurchaseResponse',
  postPurchaseUserResponse,
});

const lineItemPayloadToProduct = (payload: ProductPayload) => ({
  ...payload,
  product: lineItemToProduct(payload.product),
});

const lineItemToProduct = (product: DataLayerProduct) => ({
  ...product,
  price: product.price && product.price / 100,
});

// if a variant isn't selected we can still accept color and size by themselves
const productPayload = ({ product, variant, position, color, size, fit }: ProductPayload, quantity?: number): DataLayerProduct => {
  const variantColor = (variant && (variant.color as Color)) || color;
  const variantSize = (variant && (variant.size as LongSize)) || size;
  const variantFit = (variant && (variant.fit as Fit)) || fit;

  return removeUndefinedValues({
    name: product?.title,
    id: product?.sku,
    price: product?.price,
    brand: product?.vendor,
    category: product?.type,
    list: product?.list,
    variant: variant && variant.title,
    dimension1: variantFit,
    dimension2: variantColor,
    dimension3: variantSize,
    quantity: quantity || (variant && variant.quantity),
    position,
    // non enhanced ecommerce keys here
    tags: product?.tags,
    image_url: product?.featuredImage && product?.featuredImage.replace('.jpg', '_grande.jpg'),
    variant_id: variant && variant.sku,
    shopify_id: product?.id,
    shopify_variant_id: variant && variant.id,
    // this is used for remarketing
    viewed_variant_id: viewedVariantId(product?.sku, variant && variant.sku, variantColor, variantSize),
  });
};

const viewedVariantId = (productSku?: string, variantSku?: string, color?: Color, size?: LongSize, fit?: Fit) => {
  const skuColorMap = (c?: Color) => {
    switch (c) {
      case Color.Astral:
        return 'Eliz'; // Astral old color name was Elizabeth
      case Color.Vamp:
        return 'Cleo'; // Vamp old color name was Cleo
      default:
        return c || '';
    }
  };
  return variantSku || `${productSku}-${skuColorMap(color)}-${size || 'XL'}${fit ? `-${fit}` : ''}`;
};

const pageContext = (pathname: string) =>
  pathname.match('^/products/') ? 'Product Detail Page' : pathname.match('^/cart') ? 'Cart' : pathname.match('/checkouts/') ? 'Checkout' : 'Landing Page';

const currencyCode = (shopify?: Shopify): string => (shopify?.Checkout ? window.Shopify?.Checkout?.currency ?? '' : shopify?.currency.active ?? '');

const removeUndefinedValues = (obj: any) => Object.keys(obj).reduce((acc, key) => (obj[key] !== undefined ? { ...acc, [key]: obj[key] } : acc), {});
